import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import api from '@/lib/api/axios'

interface TranslationContextType {
    isTranslationEnabled: boolean
    toggleTranslation: () => void
    translateText: (text: string, sourceLanguage?: string) => Promise<string>
    isTranslating: boolean
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

interface TranslationProviderProps {
    children: ReactNode
}

/** In-memory cache: "targetLang:text" → translated text */
const translationCache = new Map<string, string>()

interface PendingEntry {
    text: string
    resolve: (translated: string) => void
}

export function TranslationProvider({ children }: TranslationProviderProps) {
    const { i18n } = useTranslation()
    const [isTranslationEnabled, setIsTranslationEnabled] = useState(false)
    const [isTranslating, setIsTranslating] = useState(false)

    const pendingBatch = useRef<PendingEntry[]>([])
    const batchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const flushingRef = useRef(false)

    useEffect(() => {
        const savedPreference = localStorage.getItem('translation-enabled')
        if (savedPreference !== null) {
            setIsTranslationEnabled(JSON.parse(savedPreference))
        }
    }, [])

    useEffect(() => {
        localStorage.setItem('translation-enabled', JSON.stringify(isTranslationEnabled))
    }, [isTranslationEnabled])

    const toggleTranslation = () => {
        setIsTranslationEnabled(prev => !prev)
    }

    /** Flush all pending texts in one batch API call */
    const flushBatch = useCallback(async () => {
        if (flushingRef.current || pendingBatch.current.length === 0) return
        flushingRef.current = true

        const batch = [...pendingBatch.current]
        pendingBatch.current = []

        const targetLanguage = i18n.language.split('-')[0]
        const uniqueTexts = [...new Set(batch.map(e => e.text))]

        setIsTranslating(true)
        try {
            const response = await api.post('/api/translation/translate-batch', {
                texts: uniqueTexts,
                sourceLanguage: 'en',
                targetLanguage,
            })

            const translations: Record<string, string> = response.data.translations || {}

            for (const entry of batch) {
                const translated = translations[entry.text] || entry.text
                const cacheKey = `${targetLanguage}:${entry.text}`
                translationCache.set(cacheKey, translated)
                entry.resolve(translated)
            }
        } catch (error) {
            console.error('Translation batch error:', error)
            for (const entry of batch) {
                entry.resolve(entry.text)
            }
        } finally {
            setIsTranslating(false)
            flushingRef.current = false
            if (pendingBatch.current.length > 0) {
                flushBatch()
            }
        }
    }, [i18n.language])

    const translateText = useCallback(async (text: string, sourceLanguage?: string): Promise<string> => {
        const targetLanguage = i18n.language.split('-')[0]

        if (!text || text.trim() === '') return text
        if (!isTranslationEnabled && targetLanguage === 'en') return text
        if (targetLanguage === 'en' && !isTranslationEnabled) return text

        const normalizedSource = sourceLanguage ? sourceLanguage.split('-')[0] : 'en'
        if (normalizedSource !== 'auto' && normalizedSource === targetLanguage) return text

        // Check cache
        const cacheKey = `${targetLanguage}:${text}`
        const cached = translationCache.get(cacheKey)
        if (cached) return cached

        // Queue into batch — one API call per render cycle
        return new Promise<string>((resolve) => {
            pendingBatch.current.push({ text, resolve })

            if (batchTimer.current) clearTimeout(batchTimer.current)
            batchTimer.current = setTimeout(() => {
                batchTimer.current = null
                flushBatch()
            }, 100)
        })
    }, [i18n.language, isTranslationEnabled, flushBatch])

    const value: TranslationContextType = {
        isTranslationEnabled,
        toggleTranslation,
        translateText,
        isTranslating,
    }

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    )
}

export function useTranslationToggle() {
    const context = useContext(TranslationContext)
    if (context === undefined) {
        throw new Error('useTranslationToggle must be used within a TranslationProvider')
    }
    return context
}
